import { endpointPath } from "src/hooks/useFetch";
import { ToolDefinition } from "../types";

/**
 * SQL access tools for Kate. Backed by two backend endpoints:
 *
 *   POST /api/ai/sql           { sql, maxRows? }  → rows + columns
 *   GET  /api/ai/sql/schema                       → tables + columns
 *
 * The backend is responsible for read-only enforcement, query timeouts, and
 * row capping. We add extra client-side belts that:
 *   - send a sane default maxRows (500)
 *   - stamp a hard instruction onto empty (rowCount=0) results so the
 *     model cannot treat "no rows" as a blank to fill in
 *   - truncates the JSON envelope returned to the model if it would otherwise
 *     blow past ~32KB, with a clear note so Kate knows to LIMIT or aggregate.
 */

const DEFAULT_MAX_ROWS = 500;
const MAX_PAYLOAD_CHARS = 32_000;

/**
 * Stamped onto execute_sql payloads when the query genuinely returned
 * nothing. Lives in the tool result (not only the system prompt) so the
 * model sees it on the same turn as the empty rows — the failure mode
 * we're guarding is "empty result → invent a plausible table anyway."
 */
export const EMPTY_SQL_RESULT_INSTRUCTION =
  "EMPTY RESULT: this query returned zero rows (rowCount=0). " +
  "You MUST tell the user the query returned no rows. " +
  "Do NOT invent, estimate, interpolate, or reuse numbers from earlier in this conversation. " +
  "Do NOT build tables, charts, slides, or analyses on this result. " +
  "If you think the query was wrong, say why as a hypothesis and ask whether to try a different query.";

export interface SqlResultForModel {
  columns: SqlColumn[];
  rows: unknown[][];
  rowCount: number;
  truncated: boolean;
  durationMs: number;
  instruction?: string;
}

/**
 * App-side empty-result guard. Call this before sending an execute_sql
 * envelope to the model. rowCount=0 gets a mandatory instruction; a
 * COUNT/SUM that returns a row of zeros is left alone (that's real data).
 */
export const annotateSqlResult = (
  envelope: Omit<SqlResultForModel, "instruction">,
): SqlResultForModel => {
  if (envelope.rowCount === 0) {
    return { ...envelope, instruction: EMPTY_SQL_RESULT_INSTRUCTION };
  }
  return envelope;
};

interface SqlColumn {
  name: string;
  databaseType?: string;
}

interface SqlEnvelope {
  success: boolean;
  columns?: SqlColumn[];
  rows?: unknown[][];
  rowCount: number;
  truncated: boolean;
  durationMs: number;
  error?: string;
}

interface SchemaColumn {
  name: string;
  dataType: string;
  nullable: boolean;
  columnKey?: string;
}

interface SchemaTable {
  name: string;
  columns: SchemaColumn[];
}

interface SchemaEnvelope {
  success: boolean;
  tables?: SchemaTable[];
  error?: string;
}

export const sqlToolDefinitions: ToolDefinition[] = [
  {
    name: "get_database_schema",
    description:
      "Get the read-only Telemetry database schema (tables and columns). " +
      "Call this FIRST for any non-trivial analytical question so you don't " +
      "guess at column names. The result is small and safe to call once per " +
      "conversation.",
    input_schema: {
      type: "object",
      properties: {},
    },
  },
  {
    name: "execute_sql",
    description:
      "Run a read-only SQL query against the Telemetry database. Use this " +
      "as your primary tool for any quantitative or filterable question " +
      "(counts, aggregates, joins, time windows, drill-downs). " +
      "\n\nRules:\n" +
      "- The endpoint enforces read-only; do not include INSERT/UPDATE/DELETE/DDL.\n" +
      "- ALWAYS include a LIMIT (or rely on the maxRows cap) on exploratory " +
      "queries — the database is large.\n" +
      "- ALWAYS prefer aggregation (COUNT/SUM/AVG/GROUP BY) over fetching raw " +
      "rows when the user is asking a quantitative question.\n" +
      "- Use SELECT col1, col2, ... rather than SELECT * to keep results small.\n" +
      "- Quote identifiers with double quotes if they contain spaces or " +
      "match reserved words.\n" +
      "- If the result is truncated, narrow the query and try again — never " +
      "claim a count from a truncated result set.\n" +
      "- If rowCount is 0, tell the user the query returned no rows. Do not " +
      "invent numbers, tables, or follow-on analyses.",
    input_schema: {
      type: "object",
      properties: {
        sql: {
          type: "string",
          description: "The SQL query to execute. Read-only. Should include a LIMIT for exploratory queries.",
        },
        maxRows: {
          type: "number",
          description:
            `Optional cap on returned rows. Defaults to ${DEFAULT_MAX_ROWS}. ` +
            `Aggregated queries with GROUP BY usually need much less.`,
        },
      },
      required: ["sql"],
    },
  },
];

const getProvider = ():
  | (() => Promise<string>)
  | undefined => window.__telemetryAuth?.getAccessTokenSilently;

const clipForModel = (envelope: unknown): unknown => {
  let serialized: string;
  try {
    serialized = JSON.stringify(envelope);
  } catch {
    return { error: "Could not serialize tool result" };
  }
  if (serialized.length <= MAX_PAYLOAD_CHARS) return envelope;
  return {
    note:
      `Result was ${serialized.length} chars; clipped to ${MAX_PAYLOAD_CHARS}. ` +
      "Narrow your query (more aggregation, fewer columns, smaller window).",
    clipped: serialized.slice(0, MAX_PAYLOAD_CHARS),
  };
};

export async function executeGetDatabaseSchema(): Promise<unknown> {
  const provider = getProvider();
  if (!provider) return { error: "Auth context unavailable." };

  let token: string;
  try {
    token = await provider();
  } catch (err) {
    return { error: `Could not obtain auth token: ${(err as Error).message}` };
  }

  const url = endpointPath("/ai/sql/schema");
  let res: Response;
  try {
    res = await fetch(url, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (err) {
    return { error: `Network error: ${(err as Error).message}` };
  }

  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    return { error: `Schema endpoint ${res.status}: ${text || res.statusText}` };
  }

  const envelope = (await res.json()) as SchemaEnvelope;
  if (!envelope.success) {
    return { error: envelope.error || "Schema fetch failed" };
  }
  return clipForModel({ tables: envelope.tables ?? [] });
}

export async function executeSql(input: Record<string, unknown>): Promise<unknown> {
  const sql = typeof input.sql === "string" ? input.sql : null;
  if (!sql || sql.trim().length === 0) {
    return { error: "sql is required" };
  }
  const maxRows =
    typeof input.maxRows === "number" && input.maxRows > 0
      ? Math.floor(input.maxRows)
      : DEFAULT_MAX_ROWS;

  const provider = getProvider();
  if (!provider) return { error: "Auth context unavailable." };

  let token: string;
  try {
    token = await provider();
  } catch (err) {
    return { error: `Could not obtain auth token: ${(err as Error).message}` };
  }

  const url = endpointPath("/ai/sql");
  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ sql, maxRows }),
    });
  } catch (err) {
    return { error: `Network error: ${(err as Error).message}` };
  }

  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    return { error: `SQL endpoint ${res.status}: ${text || res.statusText}` };
  }

  const envelope = (await res.json()) as SqlEnvelope;
  if (!envelope.success) {
    return { error: envelope.error || "SQL query failed" };
  }

  return clipForModel(
    annotateSqlResult({
      columns: envelope.columns ?? [],
      rows: envelope.rows ?? [],
      rowCount: envelope.rowCount,
      truncated: envelope.truncated,
      durationMs: envelope.durationMs,
    }),
  );
}
